import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Divider, Flex, Pagination, Popover, Typography } from "antd";
import ProficiencyBar from "Pages/SkillsBridgerV2/EmployeeSkillsBridger/components/ProficiencyBar";
import { CURRENT_STROKE, TEAL } from "Pages/SkillsBridgerV2/EmployeeSkillsBridger/EmployeeSkillsBridgerV2.constants";

// Reused verbatim by: Role Detail's candidate accordion, Redeployment
// Center's person accordion, and the employee-facing skill-gap detail page —
// so all three look and behave exactly like SkillsBridger's own skill view
// (req #15).

// Same paginated-list pattern as EmployeeSkillsBridgerV2 (SkillsBridger's
// own employee skills page): a client-side page of `skillGap`, plus an antd
// Pagination footer with a page-size picker. Options mirror that page's
// PAGE_SIZE_OPTIONS; the default is smaller (10 vs. 20) since one role's
// skill gap is typically much shorter than the full skills catalog that
// page paginates. hideOnSinglePage keeps the common short list (most JDs)
// free of pagination chrome, while a JD with many skills gets the same
// paged footer SkillsBridger uses.
const PAGE_SIZE_OPTIONS = ["10", "20", "30"];
const DEFAULT_PAGE_SIZE = 10;

const RowStyled = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$showScore === false ? "400px 1fr" : "400px 1fr 60px")};
  align-items: center;
  gap: 12px;
  padding: 6px 0;
`;

const HeaderRowStyled = styled(RowStyled)`
  flex-shrink: 0;
  padding: 0 0 6px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  border-bottom: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  margin-bottom: 6px;
`;

const SkillNameStyled = styled.span`
  font-size: 12.5px;
  color: ${(p) => p.theme?.colors?.dark ?? "#2B3034"};
  font-weight: ${(p) => (p.$priority ? 700 : 400)};
`;

// Mirrors SkillsBridger's own skill-name hover pattern (HrSkillsBridger's
// GroupSkillsProficiency/components/SkillNameCell) — a short description
// truncated to 2 lines under the skill name, fully readable in a Popover on
// hover — so Talent Mobility's skill views look and behave consistently with
// SkillsBridger's (req: match SkillsBridger's skill-name readout treatment).
const SkillDescriptionPopoverContent = ({ description }) => (
  <Flex vertical style={{ maxWidth: 360, maxHeight: 280, overflowY: "auto" }}>
    {description
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((para, idx) => (
        <Typography.Paragraph key={idx} style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>
          {para}
        </Typography.Paragraph>
      ))}
  </Flex>
);

const SkillNameCellStyled = ({ label, description, priority }) => (
  <Flex vertical>
    <SkillNameStyled $priority={priority}>{priority ? `★ ${label}` : label}</SkillNameStyled>
    {/* String check, not just truthiness — descriptions originate in
        schema-free API fields, and a stray object/array here would crash
        both the popover's .split and React's child rendering. */}
    {typeof description === "string" && description && (
      <Popover
        title={
          <Flex vertical gap={4}>
            <Typography.Text strong style={{ fontSize: 14 }}>
              {label}
            </Typography.Text>
            <Divider style={{ margin: 0 }} />
          </Flex>
        }
        content={<SkillDescriptionPopoverContent description={description} />}
        trigger="hover"
        placement="right"
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 11.5, marginBottom: 0 }} ellipsis={{ rows: 2 }}>
          {description}
        </Typography.Paragraph>
      </Popover>
    )}
  </Flex>
);

const ScoreStyled = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-align: right;
`;

const LegendStyled = styled.div`
  display: flex;
  gap: 16px;
  font-size: 11px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  margin-bottom: 8px;
  flex-shrink: 0;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
  background: ${(p) => p.$color};
`;

// Opt-in only (fillHeight unset = today's behavior, unchanged) — used by
// CandidateProfileV2, whose Skills tab otherwise scrolls the WHOLE panel
// (tabs bar, role/SMI title, legend and this header all included) out of
// view together. Bounding just the rows keeps the legend + column header
// ("Skill Name / Inferred Proficiency / Score") pinned in place above a
// self-contained scrollbar, the same fixed-header-over-scrollable-body
// shape as this app's own table pages (e.g. TableWrapperStyled). The
// accordion consumers (Role Detail, Redeployment Center) don't pass this —
// their lists stay natural-height inside an already-scrollable page.
//
// The bound is the space its flex parent leaves rather than a pixel figure:
// a fixed cap stops the list short of the Individual Profile Summary card
// alongside it on a tall screen, and overflows a short one. It needs a
// height-bounded flex-column ancestry to resolve against — see
// CandidateProfileV2.styles.js's RightPanelStyled/SkillsPaneStyled.
const ListStyled = styled.div`
  ${(p) =>
    p.$fill &&
    `
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `}
`;

const RowsScrollStyled = styled.div`
  ${(p) =>
    p.$fill &&
    `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  `}
`;

const SkillGapList = ({ skillGap = [], showScore = true, fillHeight = false, limit }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // A new role/candidate pair swaps `skillGap` for a different array —
  // start back on page 1 rather than stranding the view on a page that may
  // no longer exist for the new list.
  useEffect(() => {
    setCurrentPage(1);
  }, [skillGap]);

  // `limit` opts a consumer (Role Detail's accordion) into a fixed top-N
  // readout instead of the paginated list — skills already arrive ranked by
  // priority, so this is just the first N, and pagination is skipped
  // entirely since there's nothing else to page through.
  const pagedSkillGap = useMemo(() => {
    if (limit != null) return skillGap.slice(0, limit);
    const start = (currentPage - 1) * pageSize;
    return skillGap.slice(start, start + pageSize);
  }, [skillGap, currentPage, pageSize, limit]);

  if (!skillGap.length) {
    return <div style={{ fontSize: 12.5, color: "#5A6A7A" }}>No role skills available.</div>;
  }

  return (
    <ListStyled $fill={fillHeight}>
      <LegendStyled>
        <span><Dot $color={CURRENT_STROKE} />Current Proficiency</span>
        <span><Dot $color={TEAL} />Required Proficiency</span>
      </LegendStyled>
      <HeaderRowStyled $showScore={showScore}>
        <span>Skill Name</span>
        <span>Inferred Proficiency</span>
        {showScore && <span style={{ textAlign: "right" }}>Score</span>}
      </HeaderRowStyled>
      <RowsScrollStyled $fill={fillHeight}>
        {pagedSkillGap.map((skill) => (
          <RowStyled key={skill.skillId} $showScore={showScore} style={{ alignItems: "flex-start" }}>
            <SkillNameCellStyled label={skill.skillName} description={skill.description} priority={skill.priority} />
            <ProficiencyBar current={skill.current} required={skill.required} showRequired />
            {showScore && (
              <ScoreStyled style={{ color: skill.current >= skill.required ? "#28A745" : "#B45309" }}>
                {skill.current}/{skill.required}
              </ScoreStyled>
            )}
          </RowStyled>
        ))}
      </RowsScrollStyled>
      {limit == null && (
        <Pagination
          style={{ marginTop: 12, flexShrink: 0 }}
          align="end"
          current={currentPage}
          pageSize={pageSize}
          total={skillGap.length}
          onChange={(page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) setPageSize(size);
          }}
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          hideOnSinglePage
          size="small"
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} skills`}
        />
      )}
    </ListStyled>
  );
};

export default SkillGapList;
